/**
 * ``marketing.*`` event schemas — Phase 1 customer-facing surface.
 *
 * The Marketing module is the only customer module in Phase 1, so
 * this file covers the full Phase-1 module surface: dashboard, asset
 * surface, theme surface, sector surface, compare, country drilldown,
 * and the two coming-soon placeholder screens (queries / downloads).
 *
 * Backend-emitted ``marketing.export.*`` events (for ``/downloads``
 * when it ships) live on the backend side — they are not in this file.
 *
 * Source of truth: ``plans/PLAN_POSTHOG_02_EVENT_CATALOG.md`` §
 * "marketing.*". Property normalisation (ticker uppercase, country
 * alpha-2 uppercase, period enum) is enforced by the primitives in
 * ``./properties.ts``.
 */

import { z } from "zod";

import { BaseEventSchema } from "./base";
import {
  AssetClassProperty,
  CompareModeProperty,
  ConfidenceTierProperty,
  CountryCodeProperty,
  NonNegativeIntProperty,
  PeriodProperty,
  SortDirectionProperty,
  ThemeAxisProperty,
  TickerProperty,
} from "./properties";

// ── Dashboard ─────────────────────────────────────────────

export const MarketingDashboardViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.dashboard.viewed"),
  screen: z.string(),
});

export const MarketingDashboardCountryFilterChangedSchema =
  BaseEventSchema.extend({
    event_name: z.literal("marketing.dashboard.country_filter_changed"),
    country: CountryCodeProperty,
  });

// ── Asset surface ─────────────────────────────────────────

export const MarketingAssetsListViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.assets_list.viewed"),
  screen: z.string(),
});

export const MarketingAssetsListFilteredSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.assets_list.filtered"),
  asset_class: AssetClassProperty,
  include_insufficient: z.boolean(),
});

export const MarketingAssetsListSortedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.assets_list.sorted"),
  sort_field: z.string(),
  sort_direction: SortDirectionProperty,
});

export const MarketingAssetsListExportedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.assets_list.exported"),
  row_count: NonNegativeIntProperty,
});

export const MarketingAssetsListRowClickedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.assets_list.row_clicked"),
  ticker: TickerProperty,
  position: NonNegativeIntProperty,
});

export const MarketingAssetDetailViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.asset_detail.viewed"),
  screen: z.string(),
  ticker: TickerProperty,
  asset_class: AssetClassProperty,
  confidence_tier: ConfidenceTierProperty,
  period: PeriodProperty,
});

export const MarketingAssetDetailPeriodChangedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.asset_detail.period_changed"),
  ticker: TickerProperty,
  period: PeriodProperty,
  previous_period: PeriodProperty,
});

export const MarketingAssetDetailCountryFocusedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.asset_detail.country_focused"),
  ticker: TickerProperty,
  country: CountryCodeProperty,
});

// ── Theme surface ─────────────────────────────────────────

export const MarketingThemesListViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.themes_list.viewed"),
  screen: z.string(),
});

export const MarketingThemesListFilteredSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.themes_list.filtered"),
  theme_axis: ThemeAxisProperty,
  include_insufficient: z.boolean(),
});

export const MarketingThemesListSortedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.themes_list.sorted"),
  sort_field: z.string(),
  sort_direction: SortDirectionProperty,
});

export const MarketingThemesListExportedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.themes_list.exported"),
  row_count: NonNegativeIntProperty,
});

export const MarketingThemesListRowClickedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.themes_list.row_clicked"),
  theme_axis: ThemeAxisProperty,
  theme_value: z.string(),
  position: NonNegativeIntProperty,
});

export const MarketingThemeDetailViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.theme_detail.viewed"),
  screen: z.string(),
  theme_axis: ThemeAxisProperty,
  theme_value: z.string(),
  period: PeriodProperty,
});

export const MarketingThemeDetailPeriodChangedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.theme_detail.period_changed"),
  theme_axis: ThemeAxisProperty,
  theme_value: z.string(),
  period: PeriodProperty,
  previous_period: PeriodProperty,
});

// ── Sector surface ────────────────────────────────────────

export const MarketingSectorsListViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.sectors_list.viewed"),
  screen: z.string(),
});

export const MarketingSectorsListExportedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.sectors_list.exported"),
  row_count: NonNegativeIntProperty,
});

export const MarketingSectorsListRowClickedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.sectors_list.row_clicked"),
  sector: z.string(),
  position: NonNegativeIntProperty,
});

export const MarketingSectorDetailViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.sector_detail.viewed"),
  screen: z.string(),
  sector: z.string(),
});

export const MarketingSectorDetailIndustryClickedSchema =
  BaseEventSchema.extend({
    event_name: z.literal("marketing.sector_detail.industry_clicked"),
    sector: z.string(),
    industry: z.string(),
    position: NonNegativeIntProperty,
  });

export const MarketingIndustryDetailViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.industry_detail.viewed"),
  screen: z.string(),
  sector: z.string(),
  industry: z.string(),
});

// ── Compare surface ───────────────────────────────────────

export const MarketingCompareViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.viewed"),
  screen: z.string(),
});

export const MarketingCompareModeSwitchedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.mode_switched"),
  mode: CompareModeProperty,
  previous_mode: CompareModeProperty,
});

export const MarketingCompareItemAddedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.item_added"),
  mode: CompareModeProperty,
  item_id: z.string(),
  item_count_after: NonNegativeIntProperty,
});

export const MarketingCompareItemRemovedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.item_removed"),
  mode: CompareModeProperty,
  item_id: z.string(),
  item_count_after: NonNegativeIntProperty,
});

export const MarketingCompareItemBlockedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.item_blocked"),
  mode: CompareModeProperty,
  attempted_item: z.string(),
  block_reason: z.string(),
});

export const MarketingComparePeriodChangedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.period_changed"),
  mode: CompareModeProperty,
  period: PeriodProperty,
  previous_period: PeriodProperty,
});

export const MarketingCompareComposedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.compare.composed"),
  mode: CompareModeProperty,
  item_count: NonNegativeIntProperty,
});

// ── Country drilldown ─────────────────────────────────────

export const MarketingCountryDrilldownOpenedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.country_drilldown.opened"),
  country: CountryCodeProperty,
});

export const MarketingCountryDrilldownClosedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.country_drilldown.closed"),
  country: CountryCodeProperty,
  duration_ms: NonNegativeIntProperty,
});

export const MarketingCountryDrilldownPeriodChangedSchema =
  BaseEventSchema.extend({
    event_name: z.literal("marketing.country_drilldown.period_changed"),
    country: CountryCodeProperty,
    period: PeriodProperty,
    previous_period: PeriodProperty,
  });

// ── Coming-soon placeholder screens ───────────────────────

export const MarketingQueriesViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.queries.viewed"),
  screen: z.string(),
});

export const MarketingDownloadsViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("marketing.downloads.viewed"),
  screen: z.string(),
});

// ── Registry ──────────────────────────────────────────────

export const marketingEvents = [
  MarketingDashboardViewedSchema,
  MarketingDashboardCountryFilterChangedSchema,
  MarketingAssetsListViewedSchema,
  MarketingAssetsListFilteredSchema,
  MarketingAssetsListSortedSchema,
  MarketingAssetsListExportedSchema,
  MarketingAssetsListRowClickedSchema,
  MarketingAssetDetailViewedSchema,
  MarketingAssetDetailPeriodChangedSchema,
  MarketingAssetDetailCountryFocusedSchema,
  MarketingThemesListViewedSchema,
  MarketingThemesListFilteredSchema,
  MarketingThemesListSortedSchema,
  MarketingThemesListExportedSchema,
  MarketingThemesListRowClickedSchema,
  MarketingThemeDetailViewedSchema,
  MarketingThemeDetailPeriodChangedSchema,
  MarketingSectorsListViewedSchema,
  MarketingSectorsListExportedSchema,
  MarketingSectorsListRowClickedSchema,
  MarketingSectorDetailViewedSchema,
  MarketingSectorDetailIndustryClickedSchema,
  MarketingIndustryDetailViewedSchema,
  MarketingCompareViewedSchema,
  MarketingCompareModeSwitchedSchema,
  MarketingCompareItemAddedSchema,
  MarketingCompareItemRemovedSchema,
  MarketingCompareItemBlockedSchema,
  MarketingComparePeriodChangedSchema,
  MarketingCompareComposedSchema,
  MarketingCountryDrilldownOpenedSchema,
  MarketingCountryDrilldownClosedSchema,
  MarketingCountryDrilldownPeriodChangedSchema,
  MarketingQueriesViewedSchema,
  MarketingDownloadsViewedSchema,
] as const;
